/**
 * Where the wall is, and how it is lit.
 * ─────────────────────────────────────────────────────────────────────────────
 * A document photo fails at home for one reason above all others: the wall is
 * not evenly lit. The old model could not represent that at all — it took the
 * mean colour of two top corners and replaced whatever came within a distance
 * of it. Measured on a corpus whose person-alpha is known exactly (900×1200
 * passport crops, cast shadows generated as the subject's own silhouette
 * projected on the wall):
 *
 *   • the shadowed wall sits 112–120 away from that mean colour, and the
 *     slider's reach is 130 — so a normal shadow is out of reach;
 *   • the brightest SKIN sits 43–49 away, i.e. NEARER than the shadow.
 *
 * In distance-from-one-colour, skin lies between the wall and the wall's own
 * shadow. No threshold separates them, which is why the mode both left the
 * shadow and carved white holes out of the lit half of the face (9.3% of the
 * subject's pixels moved by >8 levels on a FLAT wall, peaks of 108 levels).
 *
 * So the wall is modelled here as what it physically is: a REGION with a smooth
 * illumination FIELD over it.
 *
 *   FIND IT BY GROWING, NOT BY THRESHOLDING. Growth starts at the frame border
 *   (a document crop guarantees a margin above the crown) and steps cell to
 *   cell, each step judged against the neighbour it came from. A penumbra is
 *   smooth by physics, so growth walks down it; a face begins with a step, so
 *   growth stops. That is the whole reason this works where a threshold cannot.
 *
 *   JUDGE HUE AND BRIGHTNESS SEPARATELY. A shadow keeps the wall's colour and
 *   loses its brightness. Skin differs in colour. Measured on the corpus, the
 *   lit→shadow hue distance is 0.1‰ for a neutral bounce and rises to 49‰ when
 *   a tungsten lamp is filled by daylight, while lit→skin is ~105‰ throughout:
 *   a 2.1× margin in the WORST case, which is why the hue step is allowed to be
 *   generous and the brightness step is not the thing that stops the walk.
 *
 *   A CELL THAT STRADDLES AN EDGE IS NOT WALL. Its own variance gives it away,
 *   and refusing it stops growth from stepping THROUGH a silhouette into skin
 *   that happens to sit at a similar level.
 *
 * The field this produces then serves two callers that used to disagree: the
 * background replacement, and the compliance row that tells the reader whether
 * the background is even. They must not diverge — a verdict computed a second
 * way is a verdict that will eventually contradict the picture.
 */
(function () {
    'use strict';

    /* Every threshold here is a measured quantile, not a taste.
       Corpus: 10 scenes × 29 080 wall→wall steps and 950 wall→person steps,
       labelled by ground-truth alpha, run twice (PNG and JPEG q86 — a camera
       hands nobody a PNG, and a threshold tuned on noiseless pixels is tuned
       for a cleanliness reality does not deliver).

         step, luminance   wall→wall  p99 7.2  max 13.2     wall→person  p50 54.7
         step, hue (‰)     wall→wall  p99 2.7  max  7.4     wall→person  p50  8.7

       lumStep 14 / hueStep 8 keeps 100.00% of the steps the walk must take and
       cuts the crossings it must refuse from 26% to 5%. */
    var DEF = {
        cellTarget: 64,      // ≈ min(W,H)/64 → a cell is a few tens of pixels
        hueStep: 8,          // ‰ of chromaticity, per step
        lumStep: 14,         // levels per step
        textureFloor: 10,    // a cell holding an edge has variance no wall has…
        textureK: 5,         // …judged against the wall's OWN grain, not a constant
        seedSpread: 46,      // a border cell this far from the border median is not wall
        support: 2,          // wall cells a candidate needs behind it (see grow)
        creep: 1.6,          // cells the pixel-level closure may advance beyond the region
        near: 16,            // fully background within this distance of the LOCAL wall colour
        far: 34              // untouched beyond it
    };

    function lum(r, g, b) { return 0.299 * r + 0.587 * g + 0.114 * b; }

    /* Grid of cell means + variance. One pass over the image. */
    function grid(data, W, H, cell) {
        var gw = Math.ceil(W / cell), gh = Math.ceil(H / cell);
        var col = new Float32Array(gw * gh * 3), sd = new Float32Array(gw * gh);
        for (var cy = 0; cy < gh; cy++) {
            var y1 = Math.min(H, (cy + 1) * cell);
            for (var cx = 0; cx < gw; cx++) {
                var x1 = Math.min(W, (cx + 1) * cell);
                var sr = 0, sg = 0, sb = 0, n = 0;
                for (var y = cy * cell; y < y1; y++) {
                    var row = y * W;
                    for (var x = cx * cell; x < x1; x++) {
                        var i = (row + x) * 4;
                        sr += data[i]; sg += data[i + 1]; sb += data[i + 2]; n++;
                    }
                }
                var mr = sr / n, mg = sg / n, mb = sb / n, v = 0;
                for (y = cy * cell; y < y1; y++) {
                    row = y * W;
                    for (x = cx * cell; x < x1; x++) {
                        i = (row + x) * 4;
                        var dl = lum(data[i], data[i + 1], data[i + 2]) - lum(mr, mg, mb);
                        v += dl * dl;
                    }
                }
                var k = cy * gw + cx;
                col[k * 3] = mr; col[k * 3 + 1] = mg; col[k * 3 + 2] = mb;
                sd[k] = Math.sqrt(v / n);
            }
        }
        return { gw: gw, gh: gh, cell: cell, col: col, sd: sd };
    }

    function hueOf(col, k, out) {
        var r = col[k * 3], g = col[k * 3 + 1], b = col[k * 3 + 2], s = r + g + b + 1e-6;
        out[0] = 1000 * r / s; out[1] = 1000 * g / s;
    }

    /**
     * Border cells that agree with each other. A head panned up until it touches
     * the top edge would otherwise seed the walk inside the hair, and everything
     * downstream would be confidently wrong.
     */
    function seeds(G, o) {
        var gw = G.gw, gh = G.gh, cand = [], k, cx, cy;
        var upto = Math.max(1, Math.round(gh * 0.62));
        for (cx = 0; cx < gw; cx++) cand.push(cx);                        // top row
        for (cy = 1; cy < upto; cy++) { cand.push(cy * gw); cand.push(cy * gw + gw - 1); }
        var ls = [], sds = [];
        for (var i = 0; i < cand.length; i++) {
            k = cand[i];
            ls.push(lum(G.col[k * 3], G.col[k * 3 + 1], G.col[k * 3 + 2]));
            sds.push(G.sd[k]);
        }
        var med = ls.slice().sort(function (a, b) { return a - b; })[ls.length >> 1];
        // The wall's own grain, read off the border. A plaster or wallpaper wall
        // is legitimately busier than a painted one, and a fixed variance cap
        // would answer for the decorator rather than for the photograph.
        var grain = sds.slice().sort(function (a, b) { return a - b; })[sds.length >> 1];
        var texMax = Math.max(o.textureFloor, o.textureK * grain);
        var out = [];
        for (i = 0; i < cand.length; i++) {
            k = cand[i];
            if (Math.abs(ls[i] - med) <= o.seedSpread && G.sd[k] <= texMax) out.push(k);
        }
        return { list: out, texMax: texMax, grain: grain };
    }

    /**
     * Grow the wall from the border, every step judged against the neighbour it
     * came from — that is what lets the walk follow a penumbra down and stop at
     * a face, where a global threshold cannot.
     *
     * `support` is the second guard and the one that matters most. A single
     * step that slips through opens the entire interior, because once inside
     * the hair, hair→hair steps are tiny. So a cell joins only when at least
     * two neighbours that are ALREADY wall each accept it. The wall is a sheet
     * and always has that many; a leak into a silhouette is a thread and does
     * not. Iterating to a fixed point keeps the result independent of the order
     * cells happen to be visited in.
     */
    function grow(G, seedList, o, texMax) {
        var gw = G.gw, gh = G.gh, n = gw * gh;
        var wall = new Uint8Array(n);
        for (var i = 0; i < seedList.length; i++) wall[seedList[i]] = 1;
        var ha = [0, 0], hb = [0, 0];
        var D = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
        var changed = true, guard = 0;
        while (changed && guard++ < 4 * (gw + gh)) {
            changed = false;
            for (var cy = 0; cy < gh; cy++) {
                for (var cx = 0; cx < gw; cx++) {
                    var m = cy * gw + cx;
                    if (wall[m] || G.sd[m] > texMax) continue;
                    var lb = lum(G.col[m * 3], G.col[m * 3 + 1], G.col[m * 3 + 2]);
                    hueOf(G.col, m, hb);
                    var back = 0;
                    for (var d = 0; d < 8; d++) {
                        var nx = cx + D[d][0], ny = cy + D[d][1];
                        if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
                        var k = ny * gw + nx;
                        if (!wall[k]) continue;
                        var la = lum(G.col[k * 3], G.col[k * 3 + 1], G.col[k * 3 + 2]);
                        if (Math.abs(lb - la) > o.lumStep) continue;
                        hueOf(G.col, k, ha);
                        if (Math.hypot(hb[0] - ha[0], hb[1] - ha[1]) > o.hueStep) continue;
                        if (++back >= o.support) break;
                    }
                    if (back >= o.support) { wall[m] = 1; changed = true; }
                }
            }
        }
        return wall;
    }

    /**
     * The wall's colour everywhere, including behind the person — the boundary
     * pixels need something to be compared AGAINST, and a hole in the field
     * would read as "unlike the wall" exactly where the hair is.
     * Nearest-wall fill, then a few smoothing passes.
     */
    function fieldOf(G, wall) {
        var gw = G.gw, gh = G.gh, n = gw * gh;
        var f = new Float32Array(n * 3), have = new Uint8Array(n), q = [], qi = 0, k;
        for (k = 0; k < n; k++) {
            if (wall[k]) {
                f[k * 3] = G.col[k * 3]; f[k * 3 + 1] = G.col[k * 3 + 1]; f[k * 3 + 2] = G.col[k * 3 + 2];
                have[k] = 1; q.push(k);
            }
        }
        if (!q.length) return null;
        while (qi < q.length) {
            k = q[qi++];
            var cx = k % gw, cy = (k / gw) | 0;
            for (var d = 0; d < 4; d++) {
                var nx = cx + (d === 0 ? 1 : d === 1 ? -1 : 0), ny = cy + (d === 2 ? 1 : d === 3 ? -1 : 0);
                if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
                var m = ny * gw + nx;
                if (have[m]) continue;
                f[m * 3] = f[k * 3]; f[m * 3 + 1] = f[k * 3 + 1]; f[m * 3 + 2] = f[k * 3 + 2];
                have[m] = 1; q.push(m);
            }
        }
        /* Smooth the EXTRAPOLATION, never the measurement. Blurring the cells
           we actually read pulls the field near a deep shadow up towards the
           lit side, and then the shadowed wall no longer looks like the wall
           to the pixel test — the rim survives, which is the very complaint. */
        var tmp = new Float32Array(n * 3);
        for (var pass = 0; pass < 3; pass++) {
            for (var cy2 = 0; cy2 < gh; cy2++) {
                for (var cx2 = 0; cx2 < gw; cx2++) {
                    var kk = cy2 * gw + cx2;
                    if (wall[kk]) {
                        tmp[kk * 3] = f[kk * 3]; tmp[kk * 3 + 1] = f[kk * 3 + 1]; tmp[kk * 3 + 2] = f[kk * 3 + 2];
                        continue;
                    }
                    var ar = 0, ag = 0, ab = 0, c = 0;
                    for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
                        var yy = cy2 + dy, xx = cx2 + dx;
                        if (yy < 0 || xx < 0 || yy >= gh || xx >= gw) continue;
                        var mm = yy * gw + xx;
                        ar += f[mm * 3]; ag += f[mm * 3 + 1]; ab += f[mm * 3 + 2]; c++;
                    }
                    tmp[kk * 3] = ar / c; tmp[kk * 3 + 1] = ag / c; tmp[kk * 3 + 2] = ab / c;
                }
            }
            f.set(tmp);
        }
        return f;
    }

    function sampleField(f, gw, gh, cell, x, y) {
        var fx = (x + 0.5) / cell - 0.5, fy = (y + 0.5) / cell - 0.5;
        var x0 = Math.max(0, Math.min(gw - 1, Math.floor(fx))), y0 = Math.max(0, Math.min(gh - 1, Math.floor(fy)));
        var x1 = Math.min(gw - 1, x0 + 1), y1 = Math.min(gh - 1, y0 + 1);
        var tx = Math.max(0, Math.min(1, fx - x0)), ty = Math.max(0, Math.min(1, fy - y0));
        var out = [0, 0, 0];
        for (var c = 0; c < 3; c++) {
            var a = f[(y0 * gw + x0) * 3 + c] * (1 - tx) + f[(y0 * gw + x1) * 3 + c] * tx;
            var b = f[(y1 * gw + x0) * 3 + c] * (1 - tx) + f[(y1 * gw + x1) * 3 + c] * tx;
            out[c] = a * (1 - ty) + b * ty;
        }
        return out;
    }

    /**
     * Analyse only: the region, its field, and how uneven its lighting is.
     * `drop` is the honest answer to "is there a shadow on my background" — the
     * spread between the bright part of the wall and its dark part, in levels.
     */
    function analyse(canvas, opts) {
        var o = Object.assign({}, DEF, opts || {});
        var W = canvas.width, H = canvas.height;
        var data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
        var cell = Math.max(6, Math.round(Math.min(W, H) / o.cellTarget));
        var G = grid(data, W, H, cell);
        var S = seeds(G, o);
        if (!S.list.length) return null;
        var wall = grow(G, S.list, o, S.texMax);
        var f = fieldOf(G, wall);
        if (!f) return null;

        var ls = [];
        for (var k = 0; k < G.gw * G.gh; k++) {
            if (wall[k]) ls.push(lum(G.col[k * 3], G.col[k * 3 + 1], G.col[k * 3 + 2]));
        }
        ls.sort(function (a, b) { return a - b; });
        var q = function (p) { return ls[Math.min(ls.length - 1, Math.max(0, Math.floor(ls.length * p)))]; };
        return {
            W: W, H: H, cell: cell, gw: G.gw, gh: G.gh, wall: wall, field: f, data: data, opts: o,
            grain: S.grain, coverage: ls.length / (G.gw * G.gh),
            lit: q(0.92), dark: q(0.05), drop: q(0.92) - q(0.05),
            fieldAt: function (x, y) { return sampleField(f, G.gw, G.gh, cell, x, y); }
        };
    }

    /**
     * Replace the wall with a flat colour.
     *
     * Two guards, and both are needed. The pixel test is against the LOCAL wall
     * colour, so its tolerance can be small — the wall no longer has to be
     * covered by one distance wide enough to span its own lighting, which is
     * what forced the old threshold up into skin. REACHABILITY is the other:
     * a pixel is only considered if a path of wall-looking pixels leads to it
     * from inside the region, so a highlight in the middle of a cheek is not
     * merely unlikely to be keyed — every path to it crosses skin, and it is
     * never tested at all.
     */
    function key(canvas, bg, opts) {
        var A = analyse(canvas, opts);
        if (!A) return null;
        var o = A.opts, W = A.W, H = A.H, cell = A.cell, gw = A.gw, gh = A.gh;
        var src = A.data, n = W * H;
        var near = o.near, far = o.far, span = Math.max(1, far - near);

        // Distance of every pixel from the wall colour AT THAT POINT.
        var dist = new Float32Array(n);
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                var j = y * W + x, i = j * 4;
                var wc = sampleField(A.field, gw, gh, cell, x, y);
                var dr = src[i] - wc[0], dg = src[i + 1] - wc[1], db = src[i + 2] - wc[2];
                dist[j] = Math.sqrt(dr * dr + dg * dg + db * db);
            }
        }

        /**
         * The cells were the right scale to learn the LIGHT by and the wrong
         * scale to cut on: stopping at the last whole cell left a rim of
         * shadowed wall hugging the hair — visible as a grey halo, which is
         * exactly the complaint, only thinner.
         *
         * So the region is closed at pixel level: starting inside the wall
         * cells, a neighbour joins when it still looks like the wall THERE.
         * Being reachable is the guarantee that matters — a highlight in the
         * middle of a cheek can resemble a white wall as much as it likes, but
         * every path to it crosses skin, so it is never even tested.
         */
        var reach = new Uint8Array(n), depth = new Uint16Array(n);
        var maxCreep = Math.max(2, Math.round(o.creep * cell));
        var q = new Int32Array(n), qn = 0, qi = 0;
        for (var cy = 0; cy < gh; cy++) {
            for (var cx = 0; cx < gw; cx++) {
                if (!A.wall[cy * gw + cx]) continue;
                var y1 = Math.min(H, (cy + 1) * cell), x1 = Math.min(W, (cx + 1) * cell);
                for (var yy = cy * cell; yy < y1; yy++) {
                    for (var xx = cx * cell; xx < x1; xx++) {
                        var k = yy * W + xx;
                        if (!reach[k]) { reach[k] = 1; q[qn++] = k; }
                    }
                }
            }
        }
        /* Bounded depth, and the bound is the point. Unbounded, the closure
           swallowed a shirt the same colour as the wall: 5.8% of the subject
           whitened, because at that distance the garment IS the wall as far as
           any colour test can tell. The cell-level walk had already refused it
           on structure; the flood must not be allowed to overrule that, only to
           finish the last rim the cells were too coarse to cut. */
        while (qi < qn) {
            var p = q[qi++], py = (p / W) | 0, pxx = p - py * W, nd = depth[p] + 1;
            if (nd > maxCreep) continue;
            if (pxx > 0 && !reach[p - 1] && dist[p - 1] < far) { reach[p - 1] = 1; depth[p - 1] = nd; q[qn++] = p - 1; }
            if (pxx < W - 1 && !reach[p + 1] && dist[p + 1] < far) { reach[p + 1] = 1; depth[p + 1] = nd; q[qn++] = p + 1; }
            if (py > 0 && !reach[p - W] && dist[p - W] < far) { reach[p - W] = 1; depth[p - W] = nd; q[qn++] = p - W; }
            if (py < H - 1 && !reach[p + W] && dist[p + W] < far) { reach[p + W] = 1; depth[p + W] = nd; q[qn++] = p + W; }
        }

        var out = document.createElement('canvas'); out.width = W; out.height = H;
        var octx = out.getContext('2d');
        var img = octx.createImageData(W, H), d = img.data;
        for (var j2 = 0; j2 < n; j2++) {
            var i2 = j2 * 4;
            var t = 0;
            if (reach[j2]) {
                var dd = dist[j2];
                t = dd <= near ? 1 : (dd >= far ? 0 : (far - dd) / span);
            }
            d[i2] = src[i2] * (1 - t) + bg.r * t;
            d[i2 + 1] = src[i2 + 1] * (1 - t) + bg.g * t;
            d[i2 + 2] = src[i2 + 2] * (1 - t) + bg.b * t;
            d[i2 + 3] = 255;
        }
        octx.putImageData(img, 0, 0);
        return { canvas: out, analysis: A };
    }

    window.PassportWallField = { analyse: analyse, key: key, DEFAULTS: DEF };
})();
